/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-01-07 18:47:22
 **/

import { PropsWithChildren, useEffect, useMemo, useRef } from "react";
import lottie, { AnimationDirection, AnimationItem } from "lottie-web";

function get_size(initial: number | string) {
	let size;

	if (typeof initial === "number") {
		size = `${initial}px`;
	} else {
		size = initial || "100%";
	}

	return size;
}

export default function LottiePlayer(props: PropsWithChildren<LottiePlayer.LottieProps>) {
	const { options, eventListeners = [], isStopped = false, isPaused = false } = props;
	const { loop = true, autoplay = true, animationData, rendererSettings } = options;

	const main_element_ref = useRef<HTMLDivElement>(null);
	const animation = useRef<AnimationItem | null>(null);

	useEffect(() => {
		if (main_element_ref.current) {
			animation.current = lottie.loadAnimation({
				container: main_element_ref.current,
				renderer: "svg",
				loop,
				autoplay,
				// initialSegment: segments,
				animationData,
				rendererSettings
			});

			setup_event_listener(true);
		}
		return () => {
			setup_event_listener(false);
			animation.current?.destroy();
		};
	}, []);

	const setup_event_listener = (is_install: boolean) => {
		if (animation.current) {
			for (const eventListener of eventListeners) {
				if (is_install) {
					animation.current.addEventListener(eventListener.eventName, eventListener.callback);
				} else {
					animation.current.removeEventListener(eventListener.eventName, eventListener.callback);
				}
			}
		}
	};

	const handle_click_to_pause = () => {
		if (animation.current?.isPaused) {
			animation.current?.play();
		} else {
			animation.current?.pause();
		}
	};

	const style = useMemo(
		() => ({
			width: get_size(props.width ?? 0),
			height: get_size(props.height ?? 0),
			overflow: "hidden",
			margin: "0 auto",
			outline: "none",
			...props.style
		}),
		[props.width, props.height, props.style]
	);

	useEffect(() => {
		if (isStopped) animation.current?.stop();
		if (isPaused) animation.current?.pause();
		if (!(isStopped || isPaused)) {
			animation.current?.play();
		}
	}, [isStopped, isPaused]);

	useEffect(() => {
		const { speed = 1, direction = 1 } = props;
		animation.current?.setSpeed(speed);
		animation.current?.setDirection(direction as AnimationDirection);
	}, [props]);

	return (
		<div
			ref={main_element_ref}
			style={style}
			onClick={props.isClickToPauseDisabled ? () => null : handle_click_to_pause}
			title={props.title}
			role={props.ariaRole}
			aria-label={props.ariaLabel}
			tabIndex={0}
		/>
	);
}
